import { LayoutGridIcon } from "lucide-react";

import { Link } from "@/shared.i18n/navigation";

import { PrimBreadcrumb } from "../prim.breadcrumb";
import { PrimDropdownMenu } from "../prim.dropdown-menu";
import { IconButton } from "../ui.icon-button";
import { cn } from "../util.cn";

const buildCategoryPath = (p: string[]) => {
  return `/search?c=${p.join(".")}`;
};

type BreadcrumbsItemTyped = { label: string; slugPath: string[] };

interface BreadcrumbsProps {
  paths: BreadcrumbsItemTyped[];
  size?: "1" | "2";
}

interface BreadcrumbsItemProps {
  isLastItem?: boolean;
  path: BreadcrumbsItemTyped;
}
const BreadcrumbsItem = (props: BreadcrumbsItemProps) => {
  const { isLastItem, path } = props;

  return (
    <>
      <PrimBreadcrumb.Item key={path.slugPath.join("_")}>
        <PrimBreadcrumb.Link href={buildCategoryPath(path.slugPath)}>
          {path.label}
        </PrimBreadcrumb.Link>
      </PrimBreadcrumb.Item>
      {
        isLastItem && <PrimBreadcrumb.Separator />
      }
    </>
  );
};

const Breadcrumbs = (props: BreadcrumbsProps) => {
  // length 1
  // display: Home > Coffee
  // length 2
  // display: Home > Coffee > Roasted Beans
  // length 3
  // display: Home > Coffee > Roasted Beans > Espresso
  // length 4
  // display: Home > ... > Espresso > Cappuccino

  // display rules
  // - home is always displayed
  // - the last item is always displayed
  // - if there are more than 3 items, display ellipsis

  const { paths, size = "2" } = props;

  const pathsLength = paths.length;
  const displayPaths = paths.slice(-2);
  const ellipsisPaths = pathsLength > 2 ? paths.slice(0, -2) : [];

  return (
    <PrimBreadcrumb.Root size={size}>
      <PrimBreadcrumb.List>
        <PrimBreadcrumb.Item>
          <Link href="/category" passHref>
            <IconButton
              className="my-0"
              color="gray"
              radius="full"
              variant="ghost"
              screenReaders="Home"
            >
              <LayoutGridIcon className={cn(
                size === "2" && "size-4",
                size === "1" && "size-3",
              )}
              />
            </IconButton>
          </Link>
        </PrimBreadcrumb.Item>
        {pathsLength ? <PrimBreadcrumb.Separator /> : null}

        {ellipsisPaths.length > 0 && (
          <>
            <PrimBreadcrumb.Item>
              <PrimDropdownMenu.Root>
                <PrimDropdownMenu.Trigger className={`
                  flex items-center gap-1
                `}
                >
                  <PrimBreadcrumb.Ellipsis className={cn(
                    size === "2" && "size-4",
                    size === "1" && "size-3",
                  )}
                  />
                  <span className="sr-only">Toggle menu</span>
                </PrimDropdownMenu.Trigger>
                <PrimDropdownMenu.Content align="start">
                  {ellipsisPaths.map((path, index) => {
                    return (
                      <PrimDropdownMenu.Item asChild key={path.slugPath.join("_")}>
                        <Link href={buildCategoryPath(path.slugPath)}>
                          {path.label}
                        </Link>
                      </PrimDropdownMenu.Item>

                    );
                  })}
                </PrimDropdownMenu.Content>
              </PrimDropdownMenu.Root>
            </PrimBreadcrumb.Item>
            <PrimBreadcrumb.Separator />
          </>
        )}

        {displayPaths.map((path, index) => {
          return (
            <BreadcrumbsItem
              isLastItem={(index + 1) !== displayPaths.length}
              key={path.slugPath.join("_")}
              path={path}
            />
          );
        })}
      </PrimBreadcrumb.List>
    </PrimBreadcrumb.Root>
  );
};

export { Breadcrumbs };
